import { Button, Form, FormProps } from "antd";
import { Rule } from "antd/lib/form";
import { useForm } from "antd/lib/form/Form";
import { CSSProperties } from "react";
import { Buttons } from "./Buttons";
import { Titled } from "./Titled";
import _styles from "../Interactive.module.scss";
import classNames from "classnames/bind";
import i18next from "i18next";
const styles = classNames.bind(_styles);

export type InputFormProps = {
  children: JSX.Element;
  rules?: Rule[];
  required?: boolean;
  buttonClassName?: string;
  buttonStyle?: CSSProperties;
  borderless?: boolean;
  onFinish?: (value: any) => void;
} & FormProps;

export const LargeInputForm = ({
  children,
  className,
  initialValues,
  rules = [],
  required,
  buttonClassName = styles("button"),
  buttonStyle,
  onFinish,
  mobile,
  ...rest
}: InputFormProps & { mobile?: boolean }) => {
  const [form] = useForm();
  return (
    <Form
      form={form}
      className={styles("largeInputDiv", className)}
      onFinish={({ value }) => {
        onFinish?.(value);
        form.resetFields();
      }}
      initialValues={initialValues !== undefined ? { value: initialValues } : undefined}
      {...rest}
    >
      <Form.Item
        name="value"
        required={required}
        rules={rules}
        style={{ margin: 0, flex: 1, minWidth: 0 }}
        validateFirst
      >
        {children}
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {(form) => (
          <Buttons
            style={{ marginTop: 8 }}
            mobile={mobile}
            items={[
              {
                // disabled={form.getFieldValue("value") !== undefined ? false : required}'
                name: i18next.t("input.submitForm"),
                props: {
                  disabled: form.getFieldValue("value") === undefined,
                  htmlType: "submit",
                },
                onClick: () => form.validateFields().then(form.submit),
              },
            ]}
          />
        )}
      </Form.Item>
    </Form>
  );
};

export const InputForm = ({
  children,
  className,
  initialValues,
  rules = [],
  required,
  borderless,
  buttonClassName = styles("button"),
  buttonStyle,
  onFinish,
  ...rest
}: InputFormProps) => {
  const [form] = useForm();
  return (
    <Form
      form={form}
      layout="inline"
      className={styles("inputDiv", className, { borderless })}
      onFinish={({ value }) => {
        onFinish?.(value);
        form.resetFields();
      }}
      initialValues={initialValues !== undefined ? { value: initialValues } : undefined}
      {...rest}
    >
      <Form.Item
        name="value"
        required={required}
        rules={rules}
        style={{ margin: 0, flex: 1, minWidth: 0 }}
        validateFirst
      >
        {children}
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {(form) => (
          <Button
            className={buttonClassName}
            style={buttonStyle}
            disabled={form.getFieldValue("value") !== undefined ? false : required}
            type="primary"
            htmlType="submit"
            onClick={() => form.validateFields().then(form.submit)}
          >
            {">"}
          </Button>
        )}
      </Form.Item>
    </Form>
  );
};

export const TitledInputForm = ({
  kind = "small",
  title,
  keyboard,
  keyboardPosition = "above",
  contrastGrayscale,
  mobile,
  ...rest
}: InputFormProps & {
  kind?: "small" | "large";
  title?: string;
  contrastGrayscale?: number;
  mobile?: boolean;
  keyboard?: { name: JSX.Element | string; onClick?: () => void }[];
  keyboardPosition?: "above" | "below";
}) => (
  <Titled title={title} contrastGrayscale={contrastGrayscale}>
    {keyboard && keyboardPosition === "above" && (
      <Buttons style={{ marginBottom: 8 }} mobile={mobile} items={keyboard} />
    )}
    {kind === "small" && <InputForm {...rest} />}
    {kind === "large" && <LargeInputForm mobile={mobile} {...rest} />}
    {keyboard && keyboardPosition === "below" && (
      <Buttons style={{ marginTop: 8 }} mobile={mobile} items={keyboard} />
    )}
  </Titled>
);
